import { dateUtils, listUtils, notEmpty } from '@festina-life/core'
import { type ReactNode, useState } from 'react'

import { NO_DATE_SYMBOL } from '../../util/constants'
import { AddDraftCircle } from './AddDraftCircle'
import { Timeline } from './Timeline'
import { useTimelineScale } from './useTimelineScale'

type Props<T> = {
  lengthInPixels: number
  /** Start date represented by the line */
  startDate: Date
  /** End date represented by the line */
  endDate: Date
  /** Versions that should be represented on the timeline */
  versions: T[]
  /** Function from getting the version date from a version instance */
  versionDate: (version: T) => string | symbol
  onCreateDraft?: (onDate: Date) => void
  renderDateVersions: (dateProps: {
    date: string | symbol
    versions: T[]
    dateToPixel: ReturnType<typeof useTimelineScale>['dateToPixel']
  }) => ReactNode
  hiddenTimeIntervals?: [string | null, string][]
}

export const VersionedTimeline = <T,>({
  lengthInPixels,
  startDate,
  endDate,
  versions,
  onCreateDraft,
  renderDateVersions,
  versionDate,
  hiddenTimeIntervals,
}: Props<T>) => {
  const { dateToPixel, pixelsToDate } = useTimelineScale({
    timeLineWidthPx: lengthInPixels,
    startDate,
    endDate,
  })
  const [hoverPositionX, setHoverPositionX] = useState<number>()
  const versionsByDate = listUtils.groupBy(versions, versionDate)

  const sortedVersions = [...versionsByDate]
    .map(
      ([date, dateVersions]) =>
        [typeof date === 'symbol' ? null : date, dateVersions] as const,
    )
    .sort((a, b) => dateUtils.compare(a[0], b[0]))

  return (
    <>
      <Timeline
        lengthInPixels={lengthInPixels}
        onMouseOver={setHoverPositionX}
        onMouseLeave={() => setHoverPositionX(undefined)}
        onClick={
          notEmpty(hoverPositionX) && onCreateDraft
            ? () => onCreateDraft(pixelsToDate(hoverPositionX))
            : undefined
        }
        hiddenIntervals={hiddenTimeIntervals?.map(([startDate, endDate]) => ({
          fromX: startDate
            ? dateToPixel(dateUtils.dateUtcAsLocal(startDate).toDate())
            : 0,
          toX: dateToPixel(dateUtils.dateUtcAsLocal(endDate).toDate()),
        }))}
      >
        {notEmpty(hoverPositionX) && onCreateDraft && (
          <AddDraftCircle x={hoverPositionX} />
        )}
      </Timeline>
      {sortedVersions.map(([date, versions]) =>
        renderDateVersions({
          date: date ?? NO_DATE_SYMBOL,
          versions,
          dateToPixel,
        }),
      )}
    </>
  )
}
